import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Boxes,
  CircleDollarSign,
  Eye,
  EyeOff,
  Plus,
  PlusCircle,
  RotateCw,
  Search,
  X,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/format";
import ingredientService from "@/services/ingredientService";
import { showError, showInfo } from "@/store/errorStore";
import IngredientCard from "./IngredientCard";
import IngredientFormModal from "./IngredientFormModal";
import { getPrice, isVisible, normalize } from "./ingredientUtils";

const FILTERS = {
  all: { label: "Todos", match: () => true },
  visible: { label: "Visíveis", match: (i) => isVisible(i) },
  hidden: { label: "Ocultos", match: (i) => !isVisible(i) },
  free: { label: "Preço zero", match: (i) => getPrice(i) === 0 },
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** Erros HTTP já abrem o modal global; aqui só falhas locais (sem resposta da API) */
const showLocalError = (error, fallback) => {
  if (!error?.response && !error?.request) {
    showError(error?.message || fallback, "Ingredientes");
  }
};

function MetricCard({ label, icon: Icon, iconClass, onClick, active, children }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      aria-pressed={onClick ? active : undefined}
      className={cn(
        "flex min-w-0 flex-col justify-between rounded-xl border bg-card p-3.5 text-left shadow-soft transition-all",
        onClick && "hover:shadow-ambient active:scale-[0.99]",
        active && "border-primary ring-2 ring-primary/15"
      )}
    >
      <span className="flex w-full items-center justify-between gap-2">
        <span className="truncate text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </span>
        <span className={cn("flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full", iconClass)}>
          <Icon className="h-4 w-4" />
        </span>
      </span>
      <span className="mt-2 block min-w-0">{children}</span>
    </Tag>
  );
}

function FilterChip({ active, onClick, count, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-[13px] font-semibold shadow-sm transition-colors",
        active ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:text-foreground"
      )}
    >
      {children}
      <span
        className={cn(
          "flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[10px]",
          active ? "bg-white/20" : "bg-surface-container"
        )}
      >
        {count}
      </span>
    </button>
  );
}

/**
 * Gestão de Ingredientes (adicionais dos produtos) via /ingredients.
 * A API não tem DELETE: "Ocultar" grava visible = 'N' e pode ser desfeito.
 */
function IngredientsManagement() {
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [hideTarget, setHideTarget] = useState(null);

  /**
   * `background`: recarrega sem esqueleto (após salvar/ocultar); se falhar,
   * o modal global avisa e a lista atual continua na tela. Na carga normal o
   * erro aparece no lugar da lista, com "Tentar novamente".
   */
  const loadData = useCallback(async ({ background = false } = {}) => {
    if (!background) setLoading(true);
    try {
      const data = await ingredientService.getAll({ silent: !background });
      setIngredients(Array.isArray(data) ? data : []);
      setLoadError(false);
    } catch (error) {
      if (!background) setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Texto de busca calculado uma vez por carga
  const entries = useMemo(
    () => ingredients.map((ingredient) => ({ ingredient, search: normalize(`${ingredient.name} ${ingredient.id}`) })),
    [ingredients]
  );

  const stats = useMemo(() => {
    const visibleList = ingredients.filter(isVisible);
    const paid = visibleList.map(getPrice).filter((p) => p > 0);
    return {
      total: ingredients.length,
      visible: visibleList.length,
      hidden: ingredients.length - visibleList.length,
      average: paid.length ? paid.reduce((sum, p) => sum + p, 0) / paid.length : 0,
      paidCount: paid.length,
    };
  }, [ingredients]);

  // Busca: base das contagens dos chips e da lista
  const scoped = useMemo(() => {
    const term = normalize(searchQuery);
    return term ? entries.filter((e) => e.search.includes(term)) : entries;
  }, [entries, searchQuery]);

  const counts = useMemo(() => {
    const c = {};
    Object.entries(FILTERS).forEach(([key, f]) => {
      c[key] = scoped.filter((e) => f.match(e.ingredient)).length;
    });
    return c;
  }, [scoped]);

  const shown = useMemo(
    () => scoped.filter((e) => (FILTERS[filter] || FILTERS.all).match(e.ingredient)).map((e) => e.ingredient),
    [scoped, filter]
  );

  const hasFilters = Boolean(searchQuery.trim()) || filter !== "all";

  const clearFilters = () => {
    setSearchQuery("");
    setFilter("all");
  };

  const toggleFilter = (key) => setFilter((current) => (current === key ? "all" : key));

  /* ---------- Ações ---------- */

  const openCreate = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const openEdit = useCallback((ingredient) => {
    setEditingItem(ingredient);
    setShowModal(true);
  }, []);

  // editingItem fica até a próxima abertura: limpar aqui trocaria o título
  // para "Novo Ingrediente" durante a animação de fechamento
  const handleModalChange = (open) => setShowModal(open);

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editingItem) {
        await ingredientService.update(editingItem.id, payload);
        showInfo(`"${payload.name}" atualizado`, "Ingredientes");
      } else {
        await ingredientService.create(payload);
        showInfo(`"${payload.name}" adicionado ao catálogo`, "Ingredientes");
      }
      handleModalChange(false);
      await loadData({ background: true });
    } catch (error) {
      showLocalError(error, "Erro ao salvar o ingrediente");
    } finally {
      setSaving(false);
    }
  };

  const setVisibility = useCallback(
    async (ingredient, visible) => {
      setTogglingId(ingredient.id);
      try {
        await ingredientService.setVisible(ingredient, visible);
        showInfo(
          visible ? `"${ingredient.name}" voltou ao catálogo` : `"${ingredient.name}" foi ocultado`,
          "Ingredientes"
        );
        await loadData({ background: true });
      } catch (error) {
        showLocalError(error, "Erro ao alterar a visibilidade");
      } finally {
        setTogglingId(null);
      }
    },
    [loadData]
  );

  // Ocultar pede confirmação; exibir de novo é imediato
  const handleToggleVisible = useCallback(
    (ingredient) => {
      if (isVisible(ingredient)) setHideTarget(ingredient);
      else setVisibility(ingredient, true);
    },
    [setVisibility]
  );

  const confirmHide = () => {
    const target = hideTarget;
    setHideTarget(null);
    if (target) setVisibility(target, false);
  };

  const initialLoading = loading && ingredients.length === 0;

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4">
      {/* Título e ação principal */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="truncate font-display text-xl font-bold tracking-tight text-primary md:text-2xl">
            Gestão de Ingredientes
          </h1>
          <p className="truncate text-xs text-muted-foreground md:text-sm">
            {initialLoading
              ? "Carregando catálogo..."
              : plural(stats.total, "ingrediente cadastrado", "ingredientes cadastrados")}
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="flex h-10 flex-shrink-0 items-center gap-1.5 rounded-full bg-primary px-4 text-sm font-semibold text-primary-foreground shadow-sm transition-all hover:bg-brand-dark active:scale-95"
        >
          <Plus className="h-4 w-4" />
          Novo
        </button>
      </div>

      {/* Métricas (2x2 no celular, 4 colunas no desktop) */}
      {initialLoading ? (
        <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4" aria-hidden="true">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[104px] rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
          <MetricCard label="Total" icon={Boxes} iconClass="bg-surface-container text-primary">
            <span className="block font-display text-xl font-bold text-foreground">{stats.total}</span>
            <span className="block truncate text-[11px] text-muted-foreground">Itens no catálogo</span>
          </MetricCard>
          <MetricCard
            label="Visíveis"
            icon={Eye}
            iconClass="bg-secondary text-primary"
            onClick={() => toggleFilter("visible")}
            active={filter === "visible"}
          >
            <span className="block font-display text-xl font-bold text-primary">{stats.visible}</span>
            <span className="block truncate text-[11px] font-medium text-primary">Disponíveis aos clientes</span>
          </MetricCard>
          <MetricCard
            label="Ocultos"
            icon={EyeOff}
            iconClass="bg-terra-soft text-terra"
            onClick={() => toggleFilter("hidden")}
            active={filter === "hidden"}
          >
            <span className="block font-display text-xl font-bold text-terra">{stats.hidden}</span>
            <span className="block truncate text-[11px] font-medium text-terra">Fora dos adicionais</span>
          </MetricCard>
          <MetricCard label="Preço médio" icon={CircleDollarSign} iconClass="bg-surface-container text-primary">
            <span className="block truncate font-display text-xl font-bold text-foreground">
              {formatCurrency(stats.average)}
            </span>
            <span className="block truncate text-[11px] text-muted-foreground">
              {stats.paidCount ? "Por adicional pago visível" : "Nenhum adicional pago"}
            </span>
          </MetricCard>
        </div>
      )}

      {/* Busca */}
      <div className="relative flex items-center">
        <Search className="pointer-events-none absolute left-3.5 h-4 w-4 text-muted-foreground" />
        <input
          type="search"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Pesquisar por nome ou ID..."
          aria-label="Pesquisar ingredientes"
          className="h-11 w-full rounded-xl border border-border bg-card pl-10 pr-10 text-base text-foreground shadow-sm transition-colors placeholder:text-muted-foreground/80 focus:border-primary focus:outline-none focus:ring-[3px] focus:ring-brand-pistachio/25 md:text-sm"
        />
        {searchQuery && (
          <button
            type="button"
            onClick={() => setSearchQuery("")}
            aria-label="Limpar busca"
            className="absolute right-2 flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Chips de filtro (scroll horizontal no celular) */}
      <div
        className="no-scrollbar -mx-4 flex items-center gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:px-0"
        role="group"
        aria-label="Filtrar ingredientes"
      >
        {Object.entries(FILTERS).map(([key, f]) => (
          <FilterChip key={key} active={filter === key} count={counts[key] || 0} onClick={() => setFilter(key)}>
            {f.label}
          </FilterChip>
        ))}
      </div>

      {/* Catálogo */}
      {initialLoading ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3" aria-hidden="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[118px] rounded-2xl" />
          ))}
        </div>
      ) : loadError && ingredients.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed bg-card/60 px-4 py-12 text-center">
          <Boxes className="mb-3 h-12 w-12 text-muted-foreground/40" />
          <p className="font-display text-base font-semibold text-foreground">
            Não foi possível carregar os ingredientes
          </p>
          <p className="mt-1 text-sm text-muted-foreground">Verifique a conexão e tente de novo.</p>
          <button
            type="button"
            onClick={() => loadData()}
            className="mt-4 flex h-9 items-center gap-1 rounded-full border border-border bg-surface px-3.5 text-sm font-semibold text-primary transition-colors hover:bg-muted"
          >
            <RotateCw className="h-4 w-4" />
            Tentar novamente
          </button>
        </div>
      ) : shown.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border border-dashed bg-card/60 px-4 py-12 text-center">
          <Boxes className="mb-3 h-12 w-12 text-muted-foreground/40" />
          <p className="font-display text-base font-semibold text-foreground">
            {hasFilters ? "Nenhum ingrediente encontrado" : "Nenhum ingrediente cadastrado"}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {hasFilters
              ? "Tente outra busca ou limpe os filtros."
              : "Cadastre os adicionais que o cliente pode escolher nos produtos."}
          </p>
          <button
            type="button"
            onClick={hasFilters ? clearFilters : openCreate}
            className={cn(
              "mt-4 flex h-9 items-center gap-1 rounded-full px-3.5 text-sm font-semibold transition-colors",
              hasFilters
                ? "border border-border bg-surface text-primary hover:bg-muted"
                : "bg-primary text-primary-foreground hover:bg-brand-dark"
            )}
          >
            {hasFilters ? <X className="h-4 w-4" /> : <PlusCircle className="h-4 w-4" />}
            {hasFilters ? "Limpar filtros" : "Novo ingrediente"}
          </button>
        </div>
      ) : (
        <section className="space-y-3" aria-label="Catálogo de ingredientes">
          <div className="flex items-center justify-between gap-3 px-1 text-xs text-muted-foreground">
            <span>Catálogo de Ingredientes</span>
            <span className="flex items-center gap-2">
              Mostrando {plural(shown.length, "item", "itens")}
              {hasFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="text-[13px] font-semibold text-primary hover:underline"
                >
                  Limpar filtros
                </button>
              )}
            </span>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {shown.map((ingredient) => (
              <IngredientCard
                key={ingredient.id}
                ingredient={ingredient}
                editing={showModal && editingItem?.id === ingredient.id}
                toggling={togglingId === ingredient.id}
                onEdit={openEdit}
                onToggleVisible={handleToggleVisible}
              />
            ))}
          </div>
        </section>
      )}

      <IngredientFormModal
        open={showModal}
        onOpenChange={handleModalChange}
        ingredient={editingItem}
        ingredients={ingredients}
        onSave={handleSave}
        loading={saving}
      />

      <AlertDialog open={Boolean(hideTarget)} onOpenChange={(open) => !open && setHideTarget(null)}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Ocultar ingrediente?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-semibold text-foreground">{hideTarget?.name}</span> deixará de
              aparecer como adicional no cardápio. Você pode exibi-lo de novo a qualquer momento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmHide}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Ocultar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default IngredientsManagement;
